import React, { useState, useEffect } from 'react';
import { Space, Table, Row, Col, Button, Modal, Form, Input, Typography, ConfigProvider, Tooltip, message, Popconfirm, InputNumber, Upload } from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ExclamationCircleOutlined,
    LinkOutlined,
    UploadOutlined,
    DownloadOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import API_ROUTES from '../../../api';

const { Text } = Typography;

export default function PublicationManage() {
    const [form] = Form.useForm();
    const [tableLoading, setTableLoading] = useState(false);
    const [pageData, setPageData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [totalCount, setTotalCount] = useState(0);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const handleBatchDelete = () => {
        if (selectedRowKeys.length === 0) return;
        setTableLoading(true);
        axios.post(API_ROUTES.api_publication_batch_delete, selectedRowKeys, {
            headers: { "Authorization": localStorage.getItem('userInfo') }
        })
            .then(res => {
                if (res.data.code === 200) {
                    message.success(`Successfully deleted ${selectedRowKeys.length} records`);
                    setSelectedRowKeys([]);
                    fetchData();
                } else {
                    message.error(res.data.msg);
                }
            })
            .catch(error => {
                console.error("Batch delete error", error);
                message.error("Batch delete failed");
            })
            .finally(() => {
                setTableLoading(false);
            });
    };

    const handleExport = () => {
        // Construct URL with auth if needed, but for simplicity using window.open
        // If backend requires Authorization header, might need to fetch as blob
        window.open(`${API_ROUTES.api_publication_export}?token=${localStorage.getItem('userInfo')}`);
    };

    const handleImportExcel = async ({ file }) => {
        const formData = new FormData();
        formData.append('file', file);
        setTableLoading(true);
        try {
            const res = await axios.post(API_ROUTES.api_publication_import, formData, {
                headers: { "Authorization": localStorage.getItem('userInfo') }
            });
            if (res.data.code === 200) {
                message.success('Excel import successful');
                fetchData();
            } else {
                message.error(res.data.msg);
            }
        } catch (err) {
            console.error("Import error", err);
            message.error('Excel import failed');
        } finally {
            setTableLoading(false);
        }
    };

    const handleUpload = async ({ file, onSuccess, onError }) => {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await axios.post(API_ROUTES.api_xiaoai_upload_register, formData);
            if (res.data.code === 200) {
                const newUrl = res.data.data.uploadFile;
                form.setFieldsValue({ downloadLink: newUrl });
                onSuccess(res.data.data);
                message.success('File uploaded to OSS successfully!');
            } else {
                onError(new Error(res.data.msg));
                message.error(res.data.msg);
            }
        } catch (err) {
            onError(err);
            message.error('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const fetchData = (page = currentPage, size = pageSize) => {
        setTableLoading(true);
        axios.get(API_ROUTES.api_publication_page, {
            params: {
                current: page,
                size: size
            },
            headers: {
                "Authorization": localStorage.getItem('userInfo')
            }
        })
            .then(response => {
                if (response.data.code === 200) {
                    setPageData(response.data.data.records);
                    setTotalCount(response.data.data.total);
                } else {
                    message.error(response.data.msg);
                    setPageData([]);
                    setTotalCount(0);
                }
            })
            .catch(error => {
                console.error("Publication list fetch error", error);
                message.error("Failed to fetch publication list");
                setPageData([]);
                setTotalCount(0);
            })
            .finally(() => {
                setTableLoading(false);
            });
    };

    useEffect(() => {
        fetchData();
    }, []);

    const showModal = (record = null) => {
        setEditingRecord(record);
        if (record) {
            form.setFieldsValue(record);
        } else {
            form.resetFields();
            form.setFieldsValue({ orderKey: 0 });
        }
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
        setEditingRecord(null);
    };

    const handleOk = () => {
        form.validateFields().then(values => {
            setSubmitLoading(true);
            const isEdit = !!editingRecord;
            const apiPath = isEdit ? API_ROUTES.api_publication_update : API_ROUTES.api_publication_add;
            const payload = isEdit ? { ...values, id: editingRecord.id } : values;

            axios.post(apiPath, payload, {
                headers: { "Authorization": localStorage.getItem('userInfo') }
            })
                .then(response => {
                    if (response.data.code === 200) {
                        message.success(isEdit ? 'Publication updated successfully' : 'Publication added successfully');
                        setIsModalVisible(false);
                        fetchData();
                    } else {
                        message.error(response.data.msg || 'Operation failed');
                    }
                })
                .catch(error => {
                    console.error("Publication operation error", error);
                    message.error('Operation failed');
                })
                .finally(() => {
                    setSubmitLoading(false);
                });
        }).catch(info => {
            console.log('Validate Failed:', info);
        });
    };

    const handleDelete = (record) => {
        setTableLoading(true);
        axios.post(API_ROUTES.api_publication_delete, null, {
            params: { id: record.id },
            headers: { "Authorization": localStorage.getItem('userInfo') }
        })
            .then(response => {
                if (response.data.code === 200) {
                    message.success('Publication deleted successfully');
                    fetchData();
                } else {
                    message.error(response.data.msg || 'Delete failed');
                }
            })
            .catch(error => {
                console.error("Delete error", error);
                message.error('Delete failed');
            })
            .finally(() => {
                setTableLoading(false);
            });
    };

    const columns = [
        {
            title: 'Order',
            dataIndex: 'orderKey',
            key: 'orderKey',
            width: '80px',
            align: 'center',
            sorter: (a, b) => a.orderKey - b.orderKey,
        },
        {
            title: 'Title',
            dataIndex: 'publicationName',
            key: 'publicationName',
            width: '25%',
            ellipsis: true,
            render: (text) => <Text strong style={{ color: '#055878' }}>{text}</Text>
        },
        {
            title: 'Authors',
            dataIndex: 'author',
            key: 'author',
            width: '20%',
            ellipsis: true,
        },
        {
            title: 'Journal',
            dataIndex: 'journal',
            key: 'journal',
            width: '15%',
            ellipsis: true,
            render: (text) => <Text type="secondary">{text || '-'}</Text>
        },
        {
            title: 'Year',
            dataIndex: 'year',
            key: 'year',
            width: '80px',
        },
        {
            title: 'PMID',
            dataIndex: 'pmid',
            key: 'pmid',
            width: '100px',
        },
        {
            title: 'Link',
            dataIndex: 'downloadLink',
            key: 'downloadLink',
            width: '60px',
            align: 'center',
            render: (text) => text ? (
                <Tooltip title="Download Link">
                    <a href={text} target="_blank" rel="noopener noreferrer">
                        <LinkOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                    </a>
                </Tooltip>
            ) : '-'
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: '100px',
            align: 'center',
            render: (_, record) => (
                <Space size="middle">
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            icon={<EditOutlined style={{ color: '#1890ff' }} />}
                            onClick={() => showModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Popconfirm
                            title="Delete this publication?"
                            description="Are you sure you want to delete this record?"
                            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
                            onConfirm={() => handleDelete(record)}
                            okText="Yes"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                        >
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <div style={{ background: 'white' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', alignItems: 'center' }}>
                <Text type="secondary">Manage your publication records. You can also bulk import/export via Excel.</Text>
                <Space>
                    {selectedRowKeys.length > 0 && (
                        <Popconfirm
                            title={`Delete ${selectedRowKeys.length} items?`}
                            description="This action cannot be undone."
                            onConfirm={handleBatchDelete}
                            okText="Yes"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                        >
                            <Button danger icon={<DeleteOutlined />}>
                                Batch Delete ({selectedRowKeys.length})
                            </Button>
                        </Popconfirm>
                    )}
                    <Button
                        icon={<ReloadOutlined />}
                        loading={tableLoading}
                        onClick={() => fetchData(currentPage, pageSize)}
                    >
                        Refresh
                    </Button>
                    <Button 
                        icon={<DownloadOutlined />} 
                        onClick={handleExport}
                    >
                        Export Excel
                    </Button>
                    <Upload 
                        customRequest={handleImportExcel} 
                        showUploadList={false}
                        accept=".xlsx,.xls"
                    >
                        <Button icon={<UploadOutlined />}>
                            Import Excel
                        </Button>
                    </Upload>
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />} 
                        onClick={() => showModal()}
                        style={{ background: '#055878', borderRadius: '6px' }}
                    >
                        Add Publication
                    </Button>
                </Space>
            </div>
            
            <ConfigProvider theme={{
                components: {
                    Table: {
                        headerBg: '#f0f4f8',
                        headerColor: '#055878',
                        headerBorderRadius: 8,
                        rowHoverBg: '#f8fbff'
                    }
                }
            }}>
                <Table
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={pageData}
                    rowKey="id"
                    loading={tableLoading}
                    pagination={{
                        showQuickJumper: true,
                        showSizeChanger: true,
                        defaultPageSize: 15,
                        current: currentPage,
                        total: totalCount,
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                            fetchData(page, size);
                        },
                        style: { marginTop: '24px' }
                    }}
                    scroll={{ x: 1000 }}
                    size="middle"
                    style={{ borderRadius: '8px' }}
                />
            </ConfigProvider>

            <Modal
                title={<span style={{ color: '#055878', fontWeight: 600 }}>{editingRecord ? 'Edit Publication' : 'Add New Publication'}</span>}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                confirmLoading={submitLoading}
                width={700}
                okText="Save"
                cancelText="Cancel"
                okButtonProps={{ style: { background: '#055878' } }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    style={{ marginTop: '20px' }}
                >
                    <Row gutter={16}>
                        <Col span={18}>
                            <Form.Item
                                name="publicationName"
                                label="Publication Title"
                                rules={[{ required: true, message: 'Please input the publication title!' }]}
                            >
                                <Input placeholder="e.g. Long non-coding RNA..." />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name="orderKey"
                                label="Sort Order"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <InputNumber style={{ width: '100%' }} min={0} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="author"
                        label="Authors"
                        rules={[{ required: true, message: 'Please input the authors!' }]}
                    >
                        <Input.TextArea rows={2} placeholder="e.g. Juan He#, Ke Wu#..." />
                    </Form.Item>

                    <Space style={{ display: 'flex' }} align="start">
                        <Form.Item
                            name="journal"
                            label="Journal"
                            style={{ width: '250px' }}
                            rules={[{ required: true, message: 'Please input the journal!' }]}
                        >
                            <Input placeholder="e.g. Cell Mol Life Sci" />
                        </Form.Item>
                        <Form.Item
                            name="year"
                            label="Year"
                            style={{ width: '120px' }}
                            rules={[{ required: true, message: 'Please input the year!' }]}
                        >
                            <Input placeholder="e.g. 2024" />
                        </Form.Item>
                        <Form.Item
                            name="pmid"
                            label="PMID"
                            style={{ width: '150px' }}
                        >
                            <Input placeholder="e.g. 30293090" />
                        </Form.Item>
                    </Space>

                    <Form.Item
                        name="page"
                        label="Page Info (Volume/Issue, Page Range)"
                    >
                        <Input placeholder="e.g. 75(24) 4667-4681" />
                    </Form.Item>

                    <Form.Item
                        label="Publication File (Upload to OSS)"
                        extra="Upload the PDF/File to get the download link automatically."
                    >
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Upload
                                customRequest={handleUpload}
                                showUploadList={false}
                            >
                                <Button icon={<UploadOutlined />} loading={uploading}>
                                    {form.getFieldValue('downloadLink') ? 'Change File' : 'Upload File'}
                                </Button>
                            </Upload>
                            <Form.Item
                                name="downloadLink"
                                noStyle
                            >
                                <Input
                                    placeholder="Download link (will be filled after upload)"
                                    prefix={<LinkOutlined />}
                                />
                            </Form.Item>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
